import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Clock, Camera, Sparkles } from "lucide-react";

const MEAL_LABELS = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" };
const FALLBACK = "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwZm9vZCUyMGJvd2x8ZW58MHx8fHwxNzg3ODM2NTg1fDA&ixlib=rb-4.1.0&q=85&w=200";

export default function History() {
  const navigate = useNavigate();
  const [meals, setMeals] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.listMeals().then(setMeals).catch(() => setMeals([])); }, []);

  if (meals && meals.length === 0) {
    return (
      <div className="px-5 pt-8">
        <h1 className="font-display text-2xl font-medium tracking-tight mb-8">History</h1>
        <div className="flex flex-col items-center justify-center text-center py-20 gap-4">
          <div className="w-16 h-16 rounded-3xl bg-secondary flex items-center justify-center">
            <Clock className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="font-display text-xl font-medium">Your food journey starts here.</p>
            <p className="text-muted-foreground text-sm mt-1">Scan your first meal to start tracking.</p>
          </div>
          <button onClick={() => navigate("/scan")} data-testid="history-scan-btn" className="mt-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold flex items-center gap-2 hover:bg-primary/90 transition-colors">
            <Camera className="w-4 h-4" /> Scan food
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 pt-8 space-y-4">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">History</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Every meal you've logged.</p>
      </div>
      <div className="space-y-3">
        {(meals || []).map((m) => (
          <button
            key={m.id}
            onClick={() => setOpen(m)}
            data-testid="history-item"
            className="w-full flex items-center gap-3 rounded-2xl bg-card border border-border p-3 text-left hover:border-primary/30 transition-colors"
          >
            <img src={m.image_url || FALLBACK} onError={(e) => (e.target.src = FALLBACK)} alt={m.meal_name} className="w-16 h-16 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{m.meal_name}</div>
              <div className="text-xs text-muted-foreground">
                {MEAL_LABELS[m.meal_type] || m.meal_type} · {new Date(m.created_at).toLocaleDateString("en", { month: "short", day: "numeric" })}
              </div>
            </div>
            <span className="text-sm font-display font-medium tabular-nums text-primary">{Math.round(m.calories)} kcal</span>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-sm rounded-3xl p-0 overflow-hidden gap-0" data-testid="history-detail">
          {open && (
            <div>
              <DialogTitle className="sr-only">{open.meal_name} details</DialogTitle>
              <img src={open.image_url || FALLBACK} onError={(e) => (e.target.src = FALLBACK)} alt={open.meal_name} className="w-full h-44 object-cover" />
              <div className="p-5 space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{MEAL_LABELS[open.meal_type]}</div>
                  <h3 className="font-display text-2xl font-medium tracking-tight">{open.meal_name}</h3>
                </div>
                <div className="rounded-2xl bg-primary text-primary-foreground p-3 flex items-center justify-between">
                  <span className="text-sm opacity-90">Total</span>
                  <span className="text-xl font-display font-medium">{Math.round(open.calories)} kcal</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["Protein", open.protein, "#4A5D23"], ["Carbs", open.carbs, "#E69C24"], ["Fat", open.fat, "#C45A39"], ["Fiber", open.fiber, "#8B7355"]].map(([l, v, c]) => (
                    <div key={l} className="rounded-xl bg-secondary p-2">
                      <div className="text-base font-display font-medium" style={{ color: c }}>{Math.round(v || 0)}g</div>
                      <div className="text-[10px] text-muted-foreground">{l}</div>
                    </div>
                  ))}
                </div>
                {(open.items || []).length > 0 && (
                  <div className="space-y-1.5">
                    {open.items.map((it, i) => (
                      <div key={i} className="flex justify-between text-sm border-b border-border/60 pb-1.5">
                        <span className="text-foreground/80">{it.name} {it.portion && <span className="text-muted-foreground">· {it.portion}</span>}</span>
                        <span className="font-semibold tabular-nums">{Math.round(it.calories)} kcal</span>
                      </div>
                    ))}
                  </div>
                )}
                {open.ai_insight && (
                  <div className="rounded-2xl bg-accent/8 border border-accent/20 p-3">
                    <div className="flex items-center gap-1.5 text-accent font-semibold text-sm mb-1"><Sparkles className="w-4 h-4" /> AI Insight</div>
                    <p className="text-sm text-foreground/80">{open.ai_insight}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div className="h-2" />
    </div>
  );
}
